import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FormStepper from '@/components/ui/FormStepper/FormStepper';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import SuccessScreen from '@/components/ui/SuccessScreen/SuccessScreen';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { StepDefinition } from '@/components/ui/FormStepper/types';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { useNedarimIframe, translateNedarimError, NedarimPaymentData, NEDARIM_PAYMENT_TYPE_STANDING_ORDER, NEDARIM_CURRENCY_ILS } from './useNedarimIframe';
import styles from './NewStandingOrder.module.css';

interface NedarimMosadConfig {
  mosadId: number;
  apiValid: string;
  isParentMosad: boolean;
}

interface NewStandingOrderProps {
  fundId: number;
  /** Closes the dialog this form sits in. The form has no dialog of its own. */
  onClose: () => void;
  /**
   * Where the form puts its own answer to "may the dialog close now?". The dialog around it
   * asks through this before closing, so a click outside mid-submit is ignored and a click
   * outside with a half-typed form asks first.
   */
  closeRequestRef: MutableRefObject<(() => void) | null>;
  onRefreshList?: () => void;
}

const NewStandingOrder = ({
  fundId, onClose, closeRequestRef, onRefreshList,
}: NewStandingOrderProps) => {
  const { t, i18n } = useTranslation();
  const {
    iframeRef,
    iframeKey,
    iframeUrl,
    iframeHeight,
    status,
    transactionResponse,
    handleIframeLoad,
    submitPayment,
    reset,
  } = useNedarimIframe();

  // Mounted only while the dialog is open, so there is no "open" to wait for.
  const { data: mosadConfig } = useGetAuthApi<NedarimMosadConfig>(
    fundId ? `nedarim-standing-orders/${fundId}/mosad-config` : ''
  );

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    israeliId: '',
    street: '',
    city: '',
    phone: '',
    email: '',
    amount: '',
    tashlumim: '',
    day: '',
  });

  const [amountError, setAmountError] = useState<string | null>(null);
  /**
   * Errors keyed by field. The amount had its own state and its own message beside the input;
   * the seven donor fields had neither, so a mistyped id or a mistyped address went to Nedarim
   * exactly as entered and the receipt it produced was the fund's problem to discover later.
   */
  const [donorErrors, setDonorErrors] = useState<Record<string, string>>({});
  const [showTaxWarning, setShowTaxWarning] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const taxWarningResolveRef = useRef<((value: boolean) => void) | null>(null);

  const handleTaxWarningConfirm = () => {
    taxWarningResolveRef.current?.(true);
    taxWarningResolveRef.current = null;
    setShowTaxWarning(false);
  };

  const handleTaxWarningCancel = () => {
    taxWarningResolveRef.current?.(false);
    taxWarningResolveRef.current = null;
    setShowTaxWarning(false);
  };

  const clearDonorError = (name: string) => setDonorErrors((prev) => {
    if (!prev[name]) return prev;
    const next = { ...prev };
    delete next[name];
    return next;
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    clearDonorError(e.target.name);
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = () => {
    if (!mosadConfig) return;

    const paymentData: NedarimPaymentData = {
      Mosad: String(mosadConfig.mosadId),
      ApiValid: mosadConfig.apiValid,
      PaymentType: NEDARIM_PAYMENT_TYPE_STANDING_ORDER,
      Currency: NEDARIM_CURRENCY_ILS,
      Zeout: formData.israeliId,
      FirstName: formData.firstName,
      LastName: formData.lastName,
      Street: formData.street,
      City: formData.city,
      Phone: formData.phone,
      Mail: formData.email,
      Amount: formData.amount,
      Tashlumim: formData.tashlumim,
      Day: formData.day,
      Groupe: '',
      Comment: mosadConfig.isParentMosad ? `פרויקט ${fundId}` : '',
      Param1: '',
      Param2: '',
      CallBack: '',
      CallBackMailError: '',
      ThirdPartyReceipt: '1',
    };

    submitPayment(paymentData);
  };

  const handleBackToList = () => {
    onClose();
    if (onRefreshList) onRefreshList();
  };

  const handleCreateAnother = () => {
    resetForm();
    reset();
  };

  const resetForm = () => {
    setAmountError(null);
    setFormData({
      firstName: '',
      lastName: '',
      israeliId: '',
      street: '',
      city: '',
      phone: '',
      email: '',
      amount: '',
      tashlumim: '',
      day: '',
    });
  };

  /**
   * Seven donor fields, an amount and a card, and a click outside the dialog threw all of it
   * away without a word. There is no draft and nothing to recover from, so the only protection
   * is to ask - and only when there is something to lose, so the ordinary case of opening the
   * form and changing your mind still closes on one click.
   */
  const hasUnsavedInput = () =>
    status !== 'success'
    && Object.values(formData).some((value) => value.trim() !== '');

  const requestClose = () => {
    // Closing mid-submit left the transaction in flight with nothing watching for its result.
    if (status === 'submitting') return;

    if (hasUnsavedInput()) {
      setShowDiscardWarning(true);
      return;
    }

    if (status === 'success' && onRefreshList) {
      onRefreshList();
    }
    onClose();
  };

  // Re-assigned every render so the dialog always asks the current form state, not a stale one.
  useEffect(() => {
    closeRequestRef.current = requestClose;
    return () => { closeRequestRef.current = null; };
  });

  /*
   * Up to 28 only. Nedarim take any day, but a charge booked for the 29th, 30th or 31st
   * simply does not happen in a month that has no such date, and nothing says so - the fund
   * hears about it from the donor. 28 is the last day every month has.
   */
  const orderSummaryLine = (() => {
    const amount = parseFloat(formData.amount);
    const money = amount > 0
      ? amount.toLocaleString(i18n.language, { style: 'currency', currency: 'ILS' })
      : '';
    if (!formData.day) return t('standingOrders.newOrder.summaryLineNoDay', { amount: money });
    const payments = parseInt(formData.tashlumim, 10);
    return payments > 0
      ? t('standingOrders.newOrder.summaryLineLimited', { amount: money, payments, day: formData.day })
      : t('standingOrders.newOrder.summaryLineUnlimited', { amount: money, day: formData.day });
  })();

  const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1);

  const stepDefinitions: StepDefinition[] = useMemo(() => [
    {
      content: (
        <div className={styles.stepContent}>
          <h3 className="body-md-medium text-(--font-body-3)">{t('standingOrders.donorDetails')}</h3>
          <div className={styles.fieldsGrid}>
            <div className={styles.field}>
              <label>{t('standingOrders.newOrder.firstName')}</label>
              <input name="firstName" value={formData.firstName} onChange={handleChange} maxLength={50} />
            </div>
            <div className={styles.field}>
              <label>{t('standingOrders.newOrder.lastName')}</label>
              <input name="lastName" value={formData.lastName} onChange={handleChange} maxLength={50} />
            </div>
            <div className={`${styles.field} ${donorErrors.israeliId ? styles.fieldInvalid : ''}`}>
              <label>{t('standingOrders.newOrder.israeliId')}</label>
              <input name="israeliId" value={formData.israeliId}
                onChange={(e) => { e.target.value = e.target.value.replace(/[^0-9]/g, ''); handleChange(e); }}
                inputMode="numeric" maxLength={9}
                aria-invalid={Boolean(donorErrors.israeliId)}
                aria-describedby={donorErrors.israeliId ? 'new-order-israeliId-error' : undefined}
              />
              {donorErrors.israeliId && (
                <span id="new-order-israeliId-error" role="alert" className={styles.fieldError}>
                  {donorErrors.israeliId}
                </span>
              )}
            </div>
            <div className={`${styles.field} ${donorErrors.phone ? styles.fieldInvalid : ''}`}>
              <label>{t('standingOrders.newOrder.phone')}</label>
              <input name="phone" type="tel" value={formData.phone} onChange={handleChange} maxLength={20}
                aria-invalid={Boolean(donorErrors.phone)}
                aria-describedby={donorErrors.phone ? 'new-order-phone-error' : undefined}
              />
              {donorErrors.phone && (
                <span id="new-order-phone-error" role="alert" className={styles.fieldError}>
                  {donorErrors.phone}
                </span>
              )}
            </div>
            <div className={`${styles.field} ${donorErrors.email ? styles.fieldInvalid : ''}`}>
              <label>{t('standingOrders.newOrder.email')}</label>
              <input name="email" type="email" value={formData.email} onChange={handleChange} maxLength={50}
                aria-invalid={Boolean(donorErrors.email)}
                aria-describedby={donorErrors.email ? 'new-order-email-error' : undefined}
              />
              {donorErrors.email && (
                <span id="new-order-email-error" role="alert" className={styles.fieldError}>
                  {donorErrors.email}
                </span>
              )}
            </div>
            <div className={styles.field}>
              <label>{t('standingOrders.newOrder.street')}</label>
              <input name="street" value={formData.street} onChange={handleChange} maxLength={100} />
            </div>
            <div className={styles.field}>
              <label>{t('standingOrders.newOrder.city')}</label>
              <input name="city" value={formData.city} onChange={handleChange} maxLength={100} />
            </div>
          </div>
          {/*
            * What an incomplete name or a missing id costs, said while the fields are being
            * filled rather than only in a dialog after Continue is pressed. The dialog stays,
            * because going ahead without a usable receipt is a choice somebody should make
            * on purpose - this is so they are not surprised by it.
            */}
          <p className={styles.taxNotice}>{t('standingOrders.newOrder.taxReceiptWarning')}</p>
        </div>
      ),
      validate: () => {
        /*
         * Two different things, and they were tangled into one. A value that is WRONG is
         * refused outright - an id that fails its check digit produces a receipt the donor
         * cannot use and a record nobody can match. A value that is MISSING is allowed, after
         * saying what it costs: no tax receipt. Before this, a mistyped id counted as present
         * and went through in silence, while an empty one raised a dialog about tax.
         */
        const errors: Record<string, string> = {};

        if (formData.israeliId && !isValidIsraeliID(formData.israeliId)) {
          errors.israeliId = t('standingOrders.newOrder.israeliIdInvalid');
        }

        // Deliberately loose: any Israeli number, mobile or landline, with or without a dash
        // or a leading zero, and international forms the office does receive.
        if (formData.phone && !/^\+?[\d\-\s()]{9,20}$/.test(formData.phone.trim())) {
          errors.phone = t('standingOrders.newOrder.phoneInvalid');
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.email.trim())) {
          errors.email = t('standingOrders.newOrder.emailInvalid');
        }

        setDonorErrors(errors);
        if (Object.keys(errors).length > 0) return false;

        const missingName = !formData.firstName || !formData.lastName;
        const missingId = !formData.israeliId;
        if (missingName || missingId) {
          return new Promise<boolean>((resolve) => {
            taxWarningResolveRef.current = resolve;
            setShowTaxWarning(true);
          });
        }
        return true;
      },
    },
    {
      content: (
        <div className={styles.stepContent}>
          <h3 className="body-md-medium text-(--font-body-3)">{t('standingOrders.newOrder.paymentDetails')}</h3>
          <div className={styles.fieldsGrid}>
            <div className={`${styles.field} ${amountError ? styles.fieldInvalid : ''}`}>
              <label>{t('standingOrders.newOrder.monthlyAmount')}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.inputWithPrefix}>
                <span className={styles.inputPrefix} aria-hidden="true">₪</span>
              <input
                name="amount"
                type="number"
                min="1"
                step="0.01"
                value={formData.amount}
                onChange={handleChange}
                required
                aria-invalid={Boolean(amountError)}
                aria-describedby={amountError ? 'new-order-amount-error' : undefined}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
              />
              </div>
              {amountError && (
                <span id="new-order-amount-error" role="alert" className={styles.fieldError}>
                  {amountError}
                </span>
              )}
            </div>
            <div className={styles.field}>
              <label>{t('standingOrders.newOrder.numberOfPayments')}</label>
              <input name="tashlumim" type="number" min="1" value={formData.tashlumim} onChange={handleChange} placeholder={t('standingOrders.newOrder.unlimitedPayments')} onWheel={(e) => (e.target as HTMLInputElement).blur()} />
              <span className={styles.fieldHint}>{t('standingOrders.newOrder.paymentsHint')}</span>
            </div>
            <div className={styles.field}>
              <label>{t('standingOrders.newOrder.billingDay')}</label>
              <select name="day" value={formData.day} onChange={handleChange}>
                <option value="">{t('standingOrders.newOrder.chooseBillingDay')}</option>
                {dayOptions.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <span className={styles.fieldHint}>{t('standingOrders.newOrder.billingDayHint')}</span>
            </div>
          </div>
        </div>
      ),
      validate: () => {
        // parseFloat, not truthiness: "0" and "-5" used to pass straight through to Nedarim.
        const amount = parseFloat(formData.amount);
        if (!(amount > 0)) {
          setAmountError(t('standingOrders.newOrder.amountRequired'));
          return false;
        }
        setAmountError(null);
        return true;
      },
    },
    {
      content: (
        <div className={styles.stepContent}>
          {/*
            * What is about to be created, restated before the card goes in. The two steps
            * behind this one are no longer on screen, and a card typed against the wrong
            * amount or the wrong day is a standing order the fund has to unpick with Nedarim.
            */}
          <div className={styles.summaryBox}>
            <span>
              {[formData.firstName, formData.lastName].filter(Boolean).join(' ')}
              {formData.israeliId ? ` · ${formData.israeliId}` : ''}
            </span>
            <b>{orderSummaryLine}</b>
          </div>
          <h3 className="body-md-medium text-(--font-body-3)">
            {t('standingOrders.newOrder.creditCardDetails')}
            <span className={styles.fieldHint}> ({t('standingOrders.newOrder.nedarimSecureForm')})</span>
          </h3>
          <div className={styles.iframeSection}>
            {status === 'loading' && (
              <div className={styles.iframeLoading}>{t('standingOrders.newOrder.iframeLoading')}</div>
            )}
            <iframe
              key={iframeKey}
              ref={iframeRef}
              src={iframeUrl}
              title={t('standingOrders.newOrder.creditCardDetails')}
              onLoad={handleIframeLoad}
              style={{ width: '100%', height: iframeHeight || 0, border: 'none', overflow: 'hidden' }}
            />
          </div>
          <p className={styles.pciNote}>{t('standingOrders.newOrder.pciNote')}</p>
          {status === 'error' && transactionResponse?.Message && (
            <div className={styles.errorMessage}>
              {t('standingOrders.newOrder.errorOccurred')}: {translateNedarimError(transactionResponse.Message, t)}
            </div>
          )}
        </div>
      ),
    },
  ], [formData, status, iframeHeight, transactionResponse, t, dayOptions, handleChange, iframeRef, iframeUrl, handleIframeLoad]);

  return (
    <>
      {status === 'success' ? (
        <SuccessScreen
          title={t('standingOrders.newOrder.successTitle')}
          description={t('standingOrders.newOrder.successDescription')}
          primaryButtonText={t('standingOrders.newOrder.backToList')}
          primaryButtonAction={handleBackToList}
          secondaryButtonText={t('standingOrders.newOrder.createAnother')}
          secondaryButtonAction={handleCreateAnother}
        />
      ) : (
        <FormStepper
          steps={stepDefinitions}
          onSubmit={handleSubmit}
          isSubmitting={status === 'submitting'}
        />
      )}
      <ConfirmationDialog
        open={showDiscardWarning}
        onOpenChange={(open) => { if (!open) setShowDiscardWarning(false); }}
        title={t('standingOrders.newOrder.discardTitle')}
        message={t('standingOrders.newOrder.discardMessage')}
        confirmText="standingOrders.newOrder.discardConfirm"
        cancelText="standingOrders.newOrder.discardCancel"
        variant="danger"
        onConfirm={() => { setShowDiscardWarning(false); onClose(); }}
        onCancel={() => setShowDiscardWarning(false)}
      />
      <ConfirmationDialog
        open={showTaxWarning}
        onOpenChange={(open) => { if (!open) handleTaxWarningCancel(); }}
        title={t('standingOrders.newOrder.taxReceiptWarning')}
        confirmText="standingOrders.newOrder.continueAnyway"
        cancelText="standingOrders.newOrder.backToFillDetails"
        onConfirm={handleTaxWarningConfirm}
        onCancel={handleTaxWarningCancel}
      />
    </>
  );
};

export default NewStandingOrder;
